/**
 * components/wizard/Linienpruefung.jsx — 🆕 12.10.2026 · Auftrag LP1, R2/R3.
 *
 * Die Linienprüfung im Anker-Sammelmodus, wenn die URL `liste=lp1&nr=<n>`
 * trägt. Drei Teile:
 *   · `useLinienpruefung` — Liste und Urteile laden, speichern, navigieren,
 *     zwei Klicks einsammeln (Zustand für Leiste und Ebene);
 *   · `LinienpruefungLeiste` — oben: Bild k von n, vor / zurück / nächstes
 *     offenes, Urteile je Linie, „sichtbare Linie fehlt“, Export JSON;
 *   · `LinienpruefungEbene` — über dem Frame (im Zoom-Transform): erkannte
 *     Linien farbig mit Nummer, Soll-Linien immer gestrichelt, die Klicks.
 * Geschrieben wird nur in `linien_pruefung` (Backend `routes/linienpruefung.py`).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Download, ListChecks, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  entferneLinienUrteil, exportLinienpruefung, fehlerText, getLinienpruefung,
  speichereLinienUrteil,
} from "@/lib/api";
import { sprungPfad } from "@/lib/klickliste";
import {
  FREMD_ARTEN, SOLL_HINWEIS, SOLL_ZEIGEN_VOREINSTELLUNG, ULTIMATE_LINIEN, URTEILE, bildFertig, exportName,
  fortschrittText, linienHinweis, naechster, naechsteFehltKennung, naechsterOffener, punktZu, urteilKoerper,
  urteileVonBild, vorheriger, zuBild, zuListe,
  // 🆕 44F FB2: Strich-Liste fb1
  STRICH_ARTEN, istStrichListe, naechsteStrichKennung, naechsterOhneStrich, strichArt, strichBildFertig,
  strichFortschrittText, strichKoerper, stricheVonBild,
} from "@/lib/linienpruefung";
import { LINE_CLASS_META } from "./FreeDrawTools";

const farbe = (name) => LINE_CLASS_META[name]?.color || "#e5e7eb";
const kurz = (name) => LINE_CLASS_META[name]?.short || name;

/** Zustand der Prüfung. `aktiv` false → lädt nichts, liefert `null`. */
export function useLinienpruefung({ aktiv, liste, nr, sequenceId, onZielbild }) {
  const navigate = useNavigate();
  const [daten, setDaten] = useState(null);
  const [fehler, setFehler] = useState("");
  const [busy, setBusy] = useState(false);
  // Klick-Modus: {kennung, urteil: "versetzt"|"fehlt", art?, klicks: [[x,y]…]} in Listen-Pixeln
  const [klickModus, setKlickModus] = useState(null);
  // 🆕 Nachtrag 3, R7: Soll-Linien (Pose an erkannten Linien gelöst) nur auf Wunsch
  const [sollZeigen, setSollZeigen] = useState(SOLL_ZEIGEN_VOREINSTELLUNG);

  const laden = useCallback(async () => {
    if (!aktiv) return;
    try {
      setDaten(await getLinienpruefung(liste, sequenceId));
      setFehler("");
    } catch (e) {
      setFehler(fehlerText(e, "Prüfliste nicht ladbar"));
    }
  }, [aktiv, liste, sequenceId]);

  useEffect(() => { laden(); }, [laden]);
  useEffect(() => { setKlickModus(null); }, [nr]);

  const punkt = daten ? punktZu(daten.punkte, nr) : null;
  const vonBild = useMemo(
    () => (daten && punkt ? urteileVonBild(daten.urteile, punkt.nr) : { linien: {}, fehlt: [] }),
    [daten, punkt],
  );

  // Erst springen, wenn der Punkt bekannt ist: die URL trägt t ohnehin, das
  // hier fängt einen Aufruf ohne t ab.
  useEffect(() => {
    if (aktiv && punkt && onZielbild) onZielbild(punkt.t);
    /* eslint-disable-next-line */
  }, [aktiv, punkt?.nr]);

  const geheZu = (p) => {
    if (!p) return;
    navigate(sprungPfad({ seq: sequenceId, t: p.t, liste, nr: p.nr }));
  };

  const speichern = async (body) => {
    if (!punkt) return;
    setBusy(true);
    try {
      const r = await speichereLinienUrteil(liste, sequenceId, punkt.nr, body);
      setDaten((d) => ({
        ...d,
        urteile: [
          ...(d.urteile || []).filter((u) => !(u.nr === punkt.nr && u.linie_kennung === body.linie_kennung)),
          r.urteil,
        ],
      }));
      return true;
    } catch (e) {
      toast.error(fehlerText(e, "Urteil nicht gespeichert"));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const urteilen = async (kennung, urteil, art = null) => {
    if (urteil === "versetzt") {
      setKlickModus({ kennung, urteil, klicks: [] });
      return;
    }
    setKlickModus(null);
    await speichern(urteilKoerper({ kennung, urteil, art }));
  };

  const fehltBeginnen = (art) => {
    const kennung = naechsteFehltKennung(vonBild.fehlt);
    if (!kennung) {
      toast.error("Höchstens 12 fehlende Linien je Bild");
      return;
    }
    setKlickModus({ kennung, urteil: "fehlt", art, klicks: [] });
  };

  // 🆕 44F FB2: einen Strich nachziehen (zwei Klicks), Art vorher gewählt
  const strichBeginnen = (art) => {
    const kennung = naechsteStrichKennung(stricheVonBild(daten?.urteile, punkt?.nr));
    if (!kennung) {
      toast.error("Höchstens 30 Striche je Bild");
      return;
    }
    setKlickModus({ kennung, urteil: "strich", art, klicks: [] });
  };

  const fehltEntfernen = async (kennung) => {
    if (!punkt) return;
    try {
      await entferneLinienUrteil(liste, sequenceId, punkt.nr, kennung);
      setDaten((d) => ({
        ...d,
        urteile: (d.urteile || []).filter((u) => !(u.nr === punkt.nr && u.linie_kennung === kennung)),
      }));
    } catch (e) {
      toast.error(fehlerText(e, "Eintrag nicht entfernt"));
    }
  };

  /** Ein Klick ins Bild, schon in Listen-Pixeln. Beim zweiten wird gespeichert. */
  const klick = async (p) => {
    if (!klickModus) return;
    const klicks = [...klickModus.klicks, p];
    if (klicks.length < 2) {
      setKlickModus({ ...klickModus, klicks });
      return;
    }
    const ok = await speichern(klickModus.urteil === "strich"
      ? strichKoerper({ kennung: klickModus.kennung, art: klickModus.art, klicks })
      : urteilKoerper({
        kennung: klickModus.kennung, urteil: klickModus.urteil, art: klickModus.art || null, klicks,
      }));
    if (ok) setKlickModus(null);
    else setKlickModus({ ...klickModus, klicks: [] });
  };

  const exportieren = async () => {
    try {
      const e = await exportLinienpruefung(liste, sequenceId);
      const blob = new Blob([JSON.stringify(e, null, 1)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = exportName(sequenceId, new Date(), liste);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Export: ${e.urteile.length} Urteile`);
    } catch (e) {
      toast.error(fehlerText(e, "Export fehlgeschlagen"));
    }
  };

  if (!aktiv) return null;
  return {
    daten, fehler, busy, punkt, nr, vonBild, klickModus, setKlickModus, sollZeigen, setSollZeigen,
    geheZu, urteilen, fehltBeginnen, fehltEntfernen, klick, exportieren, strichBeginnen,
  };
}

export function LinienpruefungLeiste({ lp }) {
  const [fremdArt, setFremdArt] = useState({});
  const [fehltArt, setFehltArt] = useState(ULTIMATE_LINIEN[0]);
  if (!lp) return null;
  const { daten, fehler, punkt, nr, vonBild, klickModus, busy } = lp;
  if (fehler) {
    return (
      <div data-testid="lp-leiste" className="mb-3 border border-rose-400/50 bg-rose-400/10 rounded-sm px-4 py-2 text-xs text-rose-200">
        Linienprüfung: {fehler}
      </div>
    );
  }
  if (!daten) {
    return <div data-testid="lp-leiste" className="mb-3 text-xs text-muted-foreground">Linienprüfung wird geladen …</div>;
  }
  if (istStrichListe(daten)) return <StrichLeiste lp={lp} />;      // 🆕 44F FB2
  const punkte = daten.punkte || [];
  const offen = naechsterOffener(punkte, daten.urteile, nr);
  return (
    <div data-testid="lp-leiste" className="mb-3 border border-amber-400/50 bg-card rounded-sm px-4 py-3 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <ListChecks className="w-4 h-4 text-amber-300" />
        <span data-testid="lp-fortschritt" className="font-mono text-xs">{fortschrittText(punkte, daten.urteile, nr)}</span>
        {punkt && (
          <span className="font-mono text-[11px] text-muted-foreground">
            t {punkt.t.toFixed(3)} s · Shot {punkt.shot_id} · {punkt.gruppe}
            {bildFertig(punkt, daten.urteile) ? " · fertig" : ""}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <label className="flex items-center gap-1 text-[11px] font-mono text-muted-foreground"
            title={`Soll-Linien, „Phantom“ und „m vom Soll“: ${SOLL_HINWEIS} (41f R9b), nicht aus Handankern`}>
            <input type="checkbox" data-testid="lp-soll-zeigen" checked={lp.sollZeigen}
              onChange={(e) => lp.setSollZeigen(e.target.checked)} />
            Soll-Linien zeigen
          </label>
          <Button size="sm" variant="outline" data-testid="lp-zurueck"
            disabled={!vorheriger(punkte, nr)} onClick={() => lp.geheZu(vorheriger(punkte, nr))}>
            <ChevronLeft className="w-4 h-4" /> zurück
          </Button>
          <Button size="sm" variant="outline" data-testid="lp-vor"
            disabled={!naechster(punkte, nr)} onClick={() => lp.geheZu(naechster(punkte, nr))}>
            vor <ChevronRight className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="outline" data-testid="lp-naechstes-offenes"
            disabled={!offen} onClick={() => lp.geheZu(offen?.punkt)}>
            nächstes offenes{offen?.vonVorn ? " (von vorn)" : ""}
          </Button>
          <Button size="sm" variant="outline" data-testid="lp-export" onClick={lp.exportieren}>
            <Download className="w-4 h-4" /> Export JSON
          </Button>
        </div>
      </div>
      {!punkt && <div className="text-xs text-rose-200">Bild {nr} steht nicht in der Prüfliste.</div>}
      {punkt && (
        <div className="space-y-1">
          {(punkt.linien || []).map((l) => {
            const u = vonBild.linien[l.kennung];
            return (
              <div key={l.kennung} data-testid={`lp-linie-${l.nummer}`} className="flex flex-wrap items-center gap-2 text-xs">
                <span className="font-mono w-28" style={{ color: farbe(l.name) }}>{l.nummer} · {kurz(l.name)}</span>
                {URTEILE.map((x) => (
                  <Button key={x.wert} size="sm" disabled={busy}
                    variant={u?.urteil === x.wert ? "default" : "outline"}
                    data-testid={`lp-urteil-${l.nummer}-${x.wert}`}
                    onClick={() => lp.urteilen(l.kennung, x.wert, x.wert === "fremdlinie"
                      ? (fremdArt[l.kennung] || FREMD_ARTEN[0].wert) : null)}>
                    {x.text}
                  </Button>
                ))}
                <select data-testid={`lp-fremdart-${l.nummer}`} className="bg-background border rounded-sm px-1 py-0.5"
                  value={u?.urteil === "fremdlinie" ? u.art : (fremdArt[l.kennung] || FREMD_ARTEN[0].wert)}
                  onChange={(e) => {
                    setFremdArt((f) => ({ ...f, [l.kennung]: e.target.value }));
                    if (u?.urteil === "fremdlinie") lp.urteilen(l.kennung, "fremdlinie", e.target.value);
                  }}>
                  {FREMD_ARTEN.map((a) => <option key={a.wert} value={a.wert}>{a.text}</option>)}
                </select>
                {linienHinweis(l, lp.sollZeigen) && (
                  <span data-testid={`lp-phantom-${l.nummer}`} className="text-amber-300 font-mono">{linienHinweis(l, lp.sollZeigen)}</span>
                )}
                {u && <span className="text-muted-foreground font-mono">gespeichert {u.zeit?.slice(11, 19)} UTC</span>}
              </div>
            );
          })}
          <div className="flex flex-wrap items-center gap-2 text-xs pt-1">
            <span className="w-28">Linie fehlt:</span>
            <select data-testid="lp-fehlt-art" className="bg-background border rounded-sm px-1 py-0.5"
              value={fehltArt} onChange={(e) => setFehltArt(e.target.value)}>
              {ULTIMATE_LINIEN.map((n) => <option key={n} value={n}>{LINE_CLASS_META[n]?.label || n}</option>)}
            </select>
            <Button size="sm" variant="outline" disabled={busy} data-testid="lp-fehlt"
              onClick={() => lp.fehltBeginnen(fehltArt)}>
              sichtbare Ultimate-Linie fehlt
            </Button>
            {vonBild.fehlt.map((f) => (
              <span key={f.linie_kennung} className="font-mono inline-flex items-center gap-1 border rounded-sm px-1"
                style={{ borderColor: farbe(f.art) }}>
                {f.linie_kennung} · {kurz(f.art)}
                <button type="button" data-testid={`lp-fehlt-weg-${f.linie_kennung}`} onClick={() => lp.fehltEntfernen(f.linie_kennung)}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          {klickModus && (
            <div data-testid="lp-klickmodus" className="text-xs text-amber-200 flex items-center gap-2">
              {klickModus.urteil === "versetzt"
                ? `Echte Lage von ${klickModus.kennung}: zwei Klicks ins Bild`
                : `Fehlende ${kurz(klickModus.art)}: zwei Klicks ins Bild`} ({klickModus.klicks.length} von 2)
              <Button size="sm" variant="ghost" onClick={() => lp.setKlickModus(null)}>abbrechen</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * 🆕 44F Stufe B, FB2: Leiste einer Strich-Liste (fb1). Je Bild: Art wählen, „Linie nachziehen“ (zwei Klicks),
 * die gezogenen Striche mit Entfernen; Navigation und Export wie bei der Prüfung.
 */
function StrichLeiste({ lp }) {
  const [art, setArt] = useState(STRICH_ARTEN[0].wert);
  const { daten, punkt, nr, klickModus, busy } = lp;
  const punkte = daten.punkte || [];
  const offen = naechsterOhneStrich(punkte, daten.urteile, nr);
  const striche = punkt ? stricheVonBild(daten.urteile, punkt.nr) : [];
  return (
    <div data-testid="lp-leiste" className="mb-3 border border-amber-400/50 bg-card rounded-sm px-4 py-3 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <ListChecks className="w-4 h-4 text-amber-300" />
        <span data-testid="lp-fortschritt" className="font-mono text-xs">{strichFortschrittText(punkte, daten.urteile, nr)}</span>
        {punkt && (
          <span className="font-mono text-[11px] text-muted-foreground">
            t {punkt.t.toFixed(3)} s · Shot {punkt.shot_id} · Endzone {punkt.seite || "?"}
            {strichBildFertig(punkt, daten.urteile) ? ` · ${striche.length} Striche` : ""}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" data-testid="lp-zurueck"
            disabled={!vorheriger(punkte, nr)} onClick={() => lp.geheZu(vorheriger(punkte, nr))}>
            <ChevronLeft className="w-4 h-4" /> zurück
          </Button>
          <Button size="sm" variant="outline" data-testid="lp-vor"
            disabled={!naechster(punkte, nr)} onClick={() => lp.geheZu(naechster(punkte, nr))}>
            vor <ChevronRight className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="outline" data-testid="lp-naechstes-offenes"
            disabled={!offen} onClick={() => lp.geheZu(offen?.punkt)}>
            nächstes ohne Strich{offen?.vonVorn ? " (von vorn)" : ""}
          </Button>
          <Button size="sm" variant="outline" data-testid="lp-export" onClick={lp.exportieren}>
            <Download className="w-4 h-4" /> Export JSON
          </Button>
        </div>
      </div>
      {!punkt && <div className="text-xs text-rose-200">Bild {nr} steht nicht in der Liste.</div>}
      {punkt && (
        <div className="space-y-2 text-xs">
          <div className="text-muted-foreground">
            Jede sichtbare Linie dieser Arten nachziehen, je Linie zwei Klicks auf die Linie (möglichst weit auseinander).
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select data-testid="lp-strich-art" className="bg-background border rounded-sm px-1 py-0.5"
              value={art} onChange={(e) => setArt(e.target.value)}>
              <optgroup label="quer">
                {STRICH_ARTEN.filter((a) => a.richtung === "quer").map((a) => <option key={a.wert} value={a.wert}>{a.text}</option>)}
              </optgroup>
              <optgroup label="längs">
                {STRICH_ARTEN.filter((a) => a.richtung === "längs").map((a) => <option key={a.wert} value={a.wert}>{a.text}</option>)}
              </optgroup>
            </select>
            <Button size="sm" variant="outline" disabled={busy} data-testid="lp-strich"
              onClick={() => lp.strichBeginnen(art)}>
              Linie nachziehen
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {striche.map((s) => (
              <span key={s.linie_kennung} data-testid={`lp-strich-${s.linie_kennung}`}
                className="font-mono inline-flex items-center gap-1 border rounded-sm px-1"
                style={{ borderColor: strichArt(s.art)?.farbe }}>
                {s.linie_kennung.slice(7)} · {strichArt(s.art)?.text || s.art}
                <button type="button" data-testid={`lp-strich-weg-${s.linie_kennung}`} onClick={() => lp.fehltEntfernen(s.linie_kennung)}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          {klickModus && (
            <div data-testid="lp-klickmodus" className="text-amber-200 flex items-center gap-2">
              {strichArt(klickModus.art)?.text}: zwei Klicks ins Bild ({klickModus.klicks.length} von 2)
              <Button size="sm" variant="ghost" onClick={() => lp.setKlickModus(null)}>abbrechen</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Über dem Frame, im Zoom-Transform. Fängt Klicks nur im Klick-Modus. */
export function LinienpruefungEbene({ lp, imgDims, zoom = 1 }) {
  if (!lp || !lp.daten || !lp.punkt || imgDims.w <= 1) return null;
  const { daten, punkt, vonBild, klickModus } = lp;
  const bild = daten.bild;
  const sw = Math.max(2, imgDims.w / 480) / zoom;
  const fs = Math.max(14, imgDims.w / 70) / zoom;
  const zb = (p) => zuBild(p, bild, imgDims);
  const onClick = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * imgDims.w;
    const y = ((e.clientY - r.top) / r.height) * imgDims.h;
    lp.klick(zuListe([x, y], bild, imgDims));
  };
  return (
    <>
      <svg data-testid="lp-ebene" className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox={`0 0 ${imgDims.w} ${imgDims.h}`} preserveAspectRatio="none">
        {(lp.sollZeigen ? (punkt.soll || []) : []).map((s) => {
          const [a, b] = [zb(s.p1), zb(s.p2)];
          return (
            <line key={`soll-${s.name}`} data-testid={`lp-soll-${s.name}`} x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]}
              stroke="#ffffff" strokeOpacity={0.85} strokeWidth={sw} strokeDasharray={`${4 * sw} ${3 * sw}`} />
          );
        })}
        {(punkt.linien || []).map((l) => {
          const [a, b] = [zb(l.p1), zb(l.p2)];
          const m = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
          const u = vonBild.linien[l.kennung];
          return (
            <g key={l.kennung} data-testid={`lp-erkannt-${l.nummer}`}>
              <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} stroke={farbe(l.name)} strokeWidth={1.6 * sw}
                strokeOpacity={u ? 0.55 : 1} />
              <text x={m[0]} y={m[1] - sw * 3} fill={farbe(l.name)} fontSize={fs} fontFamily="monospace"
                stroke="#000" strokeWidth={fs / 8} paintOrder="stroke">
                {l.nummer} {kurz(l.name)}{u ? ` ✓${u.urteil === "richtig" ? "" : " " + u.urteil}` : ""}
              </text>
            </g>
          );
        })}
        {Object.values(vonBild.linien).filter((u) => u.korrektur).map((u) => {
          const [a, b] = [zb(u.korrektur.p1), zb(u.korrektur.p2)];
          return <line key={`k-${u.linie_kennung}`} x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} stroke="#facc15" strokeWidth={sw} />;
        })}
        {vonBild.fehlt.map((u) => {
          const [a, b] = [zb(u.korrektur.p1), zb(u.korrektur.p2)];
          return <line key={`f-${u.linie_kennung}`} x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} stroke={farbe(u.art)} strokeWidth={sw} strokeDasharray={`${sw} ${2 * sw}`} />;
        })}
        {istStrichListe(daten) && stricheVonBild(daten.urteile, punkt.nr).map((u) => {
          // 🆕 44F FB2: gezogene Striche mit Nummer und Art
          const [a, b] = [zb(u.korrektur.p1), zb(u.korrektur.p2)];
          const fa = strichArt(u.art)?.farbe || "#e5e7eb";
          return (
            <g key={`s-${u.linie_kennung}`} data-testid={`lp-strichlinie-${u.linie_kennung}`}>
              <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} stroke={fa} strokeWidth={1.4 * sw} />
              <text x={(a[0] + b[0]) / 2} y={(a[1] + b[1]) / 2 - sw * 3} fill={fa} fontSize={fs} fontFamily="monospace"
                stroke="#000" strokeWidth={fs / 8} paintOrder="stroke">
                {u.linie_kennung.slice(7)} {strichArt(u.art)?.text || u.art}
              </text>
            </g>
          );
        })}
        {klickModus && klickModus.klicks.map((p, i) => {
          const q = zb(p);
          return <circle key={i} cx={q[0]} cy={q[1]} r={4 * sw} fill="none" stroke="#facc15" strokeWidth={sw} />;
        })}
      </svg>
      {klickModus && (
        <div data-testid="lp-klickflaeche" className="absolute inset-0 cursor-crosshair" onClick={onClick} />
      )}
    </>
  );
}
